import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
//import Swal from "sweetalert2";

import Modal from "../Modal/Modal";
import { useModal } from "../../shared/hooks/useModal";
import GenericTableComponent from "../common/Table";
import { convertToLocalCurrency } from "../../utils/currency";

import { getPayout, makePayment } from "../../redux/slices/billingSlice";

const columns = [
  { id: "publisherName", label: "Publisher" },
  { id: "createdAt", label: "Date" },
  { id: "billingMonth", label: "Payout Month" },
  { id: "conversionCount", label: "Conversion Count" },
  { id: "totalAmount", label: "Payout Amount" },
  { id: "status", label: "Status" },
];

const PayoutTable = () => {
  const dispatch = useDispatch()
  const {payouts, meta, loading} = useSelector((state) => state.billing)
  const { showModal, hideModal, isModalOpen } = useModal();

  const [selectedPayout, setSelectedPayout] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    dispatch(getPayout({ page, rowsPerPage }));
  }, [dispatch, page, rowsPerPage]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    dispatch(getPayout({ page: newPage, rowsPerPage }));
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    dispatch(getPayout({ page: 0, rowsPerPage: newRowsPerPage }));
  };

  const handleViewClick = (id) => {
    const payout = payouts.find((item) => item.id === id);
    if (!payout) return;
    setSelectedPayout(payout);
    showModal("publisher-payout");
  };

  const handleConfirmPay = async () => {
    if (!selectedPayout) return;
    const formData = new FormData();
    formData.append("billingId", selectedPayout.id);
    formData.append("partyType", "PUBLISHER");
    formData.append("amountPaid", selectedPayout.totalAmount);
    formData.append("note", "");
    formData.append("files", null);

    try {
      await dispatch(makePayment(formData)).unwrap();
      hideModal("publisher-payout");
      dispatch(getPayout({ page, rowsPerPage }));
    } catch (error) {
      console.log(error)
    }
  };

  return (
    <div>
      <GenericTableComponent
        columns={columns}
        data={payouts}
        paginate={true}
        billingView
        showModal={handleViewClick}
        page={page}
        rowsPerPage={rowsPerPage}
        totalItems={meta?.itemCount || 0}
        handlePageChange={handlePageChange}
        handleRowsPerPageChange={handleRowsPerPageChange}
      />

      <Modal
        isOpen={isModalOpen("publisher-payout")}
        label="Payout details"
        handleClose={() => hideModal("publisher-payout")}
      >
        {selectedPayout && (
          <PaymentContainer>
            <div className="header">
              <div className="flex">
                <span>●</span>
                <div>
                  <h3>{selectedPayout.publisher?.name}</h3>
                  <p>{selectedPayout.publisher?.publisherReference}</p>
                </div>
              </div>
              <StatusPill status={selectedPayout.status}>
                <span>●</span> {selectedPayout.status}
              </StatusPill>
            </div>

            <div className="amount">
              <p>Amount:</p>
              <h4>{convertToLocalCurrency(selectedPayout.totalAmount)}</h4>
            </div>

            <Details>
              <h4>Payout details</h4>
              {/* <div className="flex">
                <label>Campaign name</label>
                <p>{selectedPayout.campaignName}</p>
              </div> */}
              <div className="flex">
                <label>Billing month</label>
                <p>{selectedPayout.billingMonth}</p>
              </div>
              <div className="flex">
                <label>Conversions</label>
                <p>{selectedPayout.conversionCount}</p>
              </div>
            </Details>

            <Footer>
              <div></div>
              <div className="link-buttons">
                <button
                  type="button"
                  className="see-more"
                  onClick={() => hideModal("publisher-payout")}
                  disabled={loading}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="add-campaign"
                  onClick={handleConfirmPay}
                  disabled={loading || selectedPayout.status === "PAID"}
                  title={
                    selectedPayout.status === "PAID"
                      ? "This payout is already PAID"
                      : undefined
                  }
                >
                  {loading ? "Paying..." : "Pay publisher"}
                </button>
              </div>
            </Footer>
          </PaymentContainer>
        )}
      </Modal>
    </div>
  );
};

const PaymentContainer = styled.div`
  padding: 10px;
  max-height: 70vh;
  overflow-y: auto;
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    border-radius: 4px;
    border: 0.6px solid #b9bdc7;
    width: 100%;
    height: 60px;
    .flex {
      display: flex;
      gap: 5px;
      span {
        color: #48505e;
        font-size: 35px;
      }
      h3 {
        font-size: 16px;
        color: #383e49;
        margin-top: 7px;
        text-transform: capitalize;
      }
      p {
        color: #383e49;
        font-size: 10px;
      }
    }
  }
  .amount {
    display: flex;
    gap: 0.5em;
    margin-top: 30px;
    p {
      font-size: 14px;
      color: #1a237e;
      margin-top: 5px;
    }
    h4 {
      font-size: 24px;
      color: #1a237e;
    }
  }
`;

const Details = styled.div`
  margin-top: 30px;
  h4 {
    font-size: 18px;
    color: #101828;
    margin-bottom: 5px;
  }
  .flex {
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
    label {
      font-size: 12px;
      color: #1a237e;
    }
    p {
      font-size: 14px;
      color: #1a237e;
    }
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
  padding-bottom: 20px;
  .link-buttons {
    display: flex;
    gap: 10px;
    .see-more,
    .add-campaign {
      padding: 6px 16px;
      height: 32px;
      border: 2px solid #d0d5dd;
      border-radius: 4px;
      font-size: 12px;
      background-color: transparent;
      text-decoration: none;
      cursor: pointer;
      color: #344054;
    }
    .see-more:disabled,
    .add-campaign:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
    .add-campaign {
      color: #fff;
      border: 1px solid #03a9f4;
      background-color: #03a9f4;
    }
  }
`;

const StatusPill = styled.span`
  display: inline-block;
  font-size: 10px;
  padding: 10px;
  border-radius: 16px;
  font-weight: bold;
  text-align: center;
  text-transform: capitalize;
  span {
    font-size: 15px;
  }
  color: ${({ status }) => (status === "PAID" ? "#03a9f4" : "#AF52DE")};
  background-color: ${({ status }) =>
    status === "PAID" ? "#e8f8ff" : "rgba(186, 79, 239, 0.12)"};
`;

export default PayoutTable;